import { useEffect, useRef, useState } from 'react';
import { Check, Copy, MessageCircle } from 'lucide-react';

/**
 * "Send on WhatsApp" + copy, for a message an organizer pastes into the
 * ranking's group. `*` in the message is WhatsApp bold.
 *
 * Both actions run first thing in the tap, with nothing awaited before them:
 * iOS only allows a popup or a clipboard write inside the user gesture.
 */
export function WhatsAppMessageActions({
  message,
  label,
  onShare,
  onCopy,
  className = '',
}: {
  message: string;
  label: string;
  onShare?: () => void;
  onCopy?: () => void;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );

  const handleShare = () => {
    // wa.me opens WhatsApp's own chat picker, so the organizer lands straight on
    // the ranking's group instead of the OS share sheet.
    window.open(
      `https://wa.me/?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
    onShare?.();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      onCopy?.();
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the WhatsApp button still works */
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        onClick={handleShare}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
      >
        <MessageCircle className="w-4 h-4 text-success-green" />
        {label}
      </button>
      <button
        onClick={handleCopy}
        className="p-2 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-colors"
        title="Copiar mensagem"
      >
        {copied ? <Check className="w-4 h-4 text-success-green" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}
